<script setup lang="ts">
import CanvassDetails from "@/canvasses/CanvassDetails.vue";
import HandleQueryStatus from "@/components/HandleQueryStatus.vue";
import MainLogo from "@/components/MainLogo.vue";
import { useGetSettings } from "@/settings/settingsCrud";
import { Mail, OpenNewWindow } from "@iconoir/vue";
import { RouterLink } from "vue-router";
import { useFindCanvassByShortUrlId } from "./useFindCanvassByShortUrlId";

const { settings, getSettingsStatus } = useGetSettings();

const { shortUrlId } = defineProps<{ shortUrlId: string }>();

const { canvass, findCanvassStatus } = useFindCanvassByShortUrlId(shortUrlId);
</script>
<template>
  <HandleQueryStatus :query-status="findCanvassStatus">
    <div class="public-canvass-page container container-md">
      <header class="box column column-md">
        <MainLogo class="main-logo" />
        <div>
          <h2>Organisation</h2>
          <p class="text-large">{{ $config.organizationName }}</p>
        </div>
      </header>

      <main v-if="canvass === null || canvass === undefined" class="cover box">
        <h1>Haustüraktion wurde nicht gefunden</h1>
      </main>
      <main v-else class="column column-lg box">
        <CanvassDetails :canvass />

        <section>
          <h2 class="visually-hidden">Teilnahme</h2>
          <p
            v-if="canvass.currentUserParticipationState === 'participating'"
            class="text-semibold"
          >
            Du nimmst bereits an der Aktion teil 🎉
          </p>
          <p
            v-else-if="canvass.currentUserParticipationState === 'pending'"
            class="text-semibold"
          >
            Deine Teilnahme wurde angefragt und muss noch bestätigt werden ⌛
          </p>
          <RouterLink
            v-else
            :to="{
              name: 'participationRequest',
              params: { canvassId: canvass.id },
            }"
            class="button participate-button"
          >
            Teilnehmen
          </RouterLink>
        </section>
      </main>

      <footer>
        <nav aria-labelledby="footer-nav-label">
          <h2 id="footer-nav-label" class="visually-hidden">
            Footernavigation
          </h2>
          <HandleQueryStatus :query-status="getSettingsStatus">
            <ul role="list" class="row">
              <li>
                <a href="mailto:kiezkompass@aboutsource.net"
                  ><Mail /> Support</a
                >
              </li>
              <li>
                <a
                  :href="settings?.privacyPolicyUrl"
                  aria-label="Datenschutzerklärung - Link öffnet einen neuen Tab"
                  target="_blank"
                  rel="noopener noreferrer"
                  ><OpenNewWindow /> Datenschutz</a
                >
              </li>
              <li>
                <a
                  :href="settings?.impressUrl"
                  aria-label="Impressum - Link öffnet einen neuen Tab"
                  target="_blank"
                  rel="noopener noreferrer"
                  ><OpenNewWindow /> Impressum</a
                >
              </li>
            </ul>
          </HandleQueryStatus>
        </nav>
      </footer>
    </div>
  </HandleQueryStatus>
</template>

<style scoped>
.public-canvass-page {
  block-size: 100vh;
  display: flex;
  flex-direction: column;
}

h1,
h2 {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);
}

main {
  flex-grow: 1;
}

footer {
  position: sticky;
  inset-block-end: 0;
  padding-block: 1rem;
  background-color: var(--bg-color-main);

  nav {
    display: flex;
    justify-content: center;
  }
}

.text-large {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
}

.main-logo {
  block-size: 4rem;
}

.participate-button {
  inline-size: 100%;
  justify-content: center;
}
</style>
